import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import PipelineStepper, { ADMIN_DEV_STEP_META } from "@/components/simulator/PipelineStepper";
import StepFeedback from "@/components/simulator/StepFeedback";
import IdentityStep from "@/components/simulator/IdentityStep";
import SingleChoiceStep from "@/components/simulator/SingleChoiceStep";

const STEPS = ["objects", "security", "automation", "apex_deploy"];

const STEP_COPY = {
  objects: { title: "Step 1 — Data Model & Custom Objects", description: "Select every Standard/Custom Object and relationship this case study actually needs. Watch out for decoys that sound plausible but aren't required." },
  security: { title: "Step 2 — Security & Sharing Model", description: "Select the OWD/Role Hierarchy/Sharing Rule combination that gives the right people visibility — no more, no less." },
  automation: { title: "Step 3 — Automation Tool Choice", description: "Pick the single best-fit automation tool for this specific requirement." },
  apex_deploy: { title: "Step 4 — Apex & Deployment Best Practices", description: "Select every best practice this case study should follow for safe, bulk-safe, well-tested code." },
};

const emptyAnswers = () => ({ objects: [], security: [], automation: null, apex_deploy: [] });

/** Admin + Dev-specific case study simulator tab. Uses the same 4-step
 * multi-select/single-choice engine as the standalone Admin + Dev Simulator
 * (admin_dev_simulator_engine.py) instead of CaseStudySimulatorTab.js's
 * 7-step Data Cloud pipeline shape, since the domains don't map onto each other. */
export default function AdminDevCaseStudySimulatorTab({ caseStudyId, scenario, initialSteps, onAllDone, disabled, apiPrefix }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState(() => {
    const next = emptyAnswers();
    const done = initialSteps || {};
    for (const step of STEPS) {
      if (!done[step]) continue;
      const ans = done[step].answer || {};
      if (step === "automation") next.automation = ans.selected_id || null;
      else next[step] = ans.selected_ids || [];
    }
    return next;
  });
  const [results, setResults] = useState(() => {
    const next = {};
    const done = initialSteps || {};
    for (const step of STEPS) {
      if (!done[step]) continue;
      next[step] = { score: done[step].score, ai_feedback: done[step].ai_feedback, is_correct: done[step].score >= 80 };
    }
    return next;
  });
  const [checking, setChecking] = useState(false);

  const currentStep = STEPS[stepIndex];
  const result = results[currentStep];
  const isLast = stepIndex === STEPS.length - 1;
  const allDone = STEPS.every((s) => !!results[s]);

  const buildPayload = () => {
    if (currentStep === "automation") return { selected_id: answers.automation };
    return { selected_ids: answers[currentStep] };
  };

  const checkStep = async () => {
    setChecking(true);
    try {
      const { data } = await api.post(`${apiPrefix}/${caseStudyId}/simulator/steps/${currentStep}`, buildPayload());
      const next = { ...results, [currentStep]: data };
      setResults(next);
      if (STEPS.every((s) => !!next[s])) onAllDone(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to check this step");
    } finally {
      setChecking(false);
    }
  };

  const copy = STEP_COPY[currentStep];

  return (
    <div data-testid="admin-dev-cs-simulator-tab">
      <PipelineStepper steps={STEPS} currentStep={currentStep} results={results} onJump={setStepIndex} stepMeta={ADMIN_DEV_STEP_META} />

      <div className="glass-panel rounded-2xl p-6">
        {currentStep === "automation" ? (
          <SingleChoiceStep options={scenario.automation.options} selectedId={answers.automation} onChange={(v) => setAnswers((p) => ({ ...p, automation: v }))} title={copy.title} description={copy.description} disabled={disabled} />
        ) : (
          <IdentityStep options={scenario[currentStep].options} selected={answers[currentStep]} onChange={(v) => setAnswers((p) => ({ ...p, [currentStep]: v }))} title={copy.title} description={copy.description} disabled={disabled} />
        )}

        <StepFeedback result={result} />

        <div className="flex items-center justify-between mt-6 pt-6 border-t border-white/5">
          <button
            onClick={() => setStepIndex((i) => Math.max(0, i - 1))} disabled={stepIndex === 0} data-testid="admin-dev-cs-sim-prev-step-btn"
            className="text-sm font-semibold text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
          >
            Previous
          </button>
          {!disabled && (
            <button
              onClick={checkStep} disabled={checking} data-testid="admin-dev-cs-sim-check-step-btn"
              className="flex items-center gap-2 bg-white/10 hover:bg-white/15 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
            >
              {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {result ? "Re-check with AI" : "Check My Work"}
            </button>
          )}
          {!isLast ? (
            <button
              onClick={() => setStepIndex((i) => Math.min(STEPS.length - 1, i + 1))} data-testid="admin-dev-cs-sim-next-step-btn"
              className="text-sm font-semibold text-slate-400 hover:text-white transition-colors"
            >
              Next
            </button>
          ) : allDone ? (
            <span className="flex items-center gap-1.5 text-sm font-semibold text-emerald-400" data-testid="admin-dev-cs-sim-all-done-badge">
              <CheckCircle2 className="w-4 h-4" /> All steps checked
            </span>
          ) : (
            <span className="w-16" />
          )}
        </div>
      </div>
    </div>
  );
}
